import React from "react";
import { motion } from "framer-motion";
import { getIcon } from "@/lib/icons";
import { CheckCircle2, XCircle, Coins, Trash2 } from "lucide-react";
import Seo from "@/components/Seo";
import SectionHeading from "@/components/SectionHeading";
import { useContent } from "@/context/ContentContext";
import { Link } from "react-router-dom";

function IconCard({ item, tone, i }) {
  const Icon = getIcon(item.icon);
  const toneMap = {
    included: "text-[#21e6cf]",
    not: "text-[#e0b52e]",
    pay: "text-[#ff3b3b]",
  };
  const bgMap = {
    included: "bg-[#0e2036]",
    not: "bg-[#2a2810]",
    pay: "bg-[#241028]",
  };
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.04 }}
      className="card card-lift p-5 flex items-center gap-4" data-testid={`included-item-${item.label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
      <div className={`shrink-0 grid place-items-center h-12 w-12 rounded-xl ${bgMap[tone]} ${toneMap[tone]}`}><Icon size={24} /></div>
      <span className="font-semibold text-[#f5f0ff]">{item.label}</span>
    </motion.div>
  );
}

export default function WhatsIncluded() {
  const { content } = useContent();
  const inc = content?.whats_included;
  if (!inc) return <div className="min-h-screen" />;
  return (
    <div className="pt-32 pb-24">
      <Seo title="What's Included | Keys Event Center" description="See everything included in your private reservation: entire facility, kitchenette, unlimited play, arcade, air hockey, foosball, RC cars and more." />
      <div className="mx-auto max-w-7xl px-6">
        <SectionHeading eyebrow="Book The Whole Place" title="What's Included" subtitle="One reservation, the entire facility. Here's everything your group gets to enjoy." />

        <div className="flex items-center gap-3 mb-6"><CheckCircle2 className="text-[#21e6cf]" /><h3 className="font-display text-2xl font-bold text-[#f5f0ff]">Included With Every Reservation</h3></div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 mb-16">
          {inc.included.map((item, i) => <IconCard key={item.label} item={item} tone="included" i={i} />)}
        </div>

        <div className="grid lg:grid-cols-2 gap-10">
          <div>
            <div className="flex items-center gap-3 mb-6"><XCircle className="text-[#e0b52e]" /><h3 className="font-display text-2xl font-bold text-[#f5f0ff]">Bring Your Own</h3></div>
            <div className="grid gap-4 sm:grid-cols-2">{inc.not_included.map((item, i) => <IconCard key={item.label} item={item} tone="not" i={i} />)}</div>
          </div>
          <div>
            <div className="flex items-center gap-3 mb-6"><Coins className="text-[#ff3b3b]" /><h3 className="font-display text-2xl font-bold text-[#f5f0ff]">Pay To Play</h3></div>
            <div className="grid gap-4 sm:grid-cols-2">{inc.pay_to_play.map((item, i) => <IconCard key={item.label} item={item} tone="pay" i={i} />)}</div>
            <div className="card p-5 mt-4 flex items-start gap-3">
              <Trash2 className="text-[#7a7fa0] shrink-0 mt-0.5" size={20} />
              <p className="text-sm text-[#9aa0c0]"><span className="text-[#f5f0ff] font-semibold">Guest responsibilities:</span> {inc.responsibilities.join(" and ")}. Please leave the space as you found it.</p>
            </div>
          </div>
        </div>

        <div className="text-center mt-16">
          <Link to="/reservations" className="btn-primary inline-flex px-8 py-4 text-base" data-testid="included-reserve-cta">Reserve Your Party</Link>
        </div>
      </div>
    </div>
  );
}
